import { useRef, useState } from 'react';
import { MdSchedule } from 'react-icons/md';

import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';
import type {
  AgentApiCandidateMailsRecord,
  AgentApiCandidateMailsShow,
  AgentApiOrganizationsMailSetting,
} from '@/types/serializers';
import useCancelCandidateMail from '../api/useCancelCandidateMail';
import useFetchCandidateMail from '../api/useFetchCandidateMail';
import useRescheduleCandidateMail from '../api/useRescheduleCandidateMail';
import {
  formatMailHeadingDate,
  formatScheduledAt,
  parseServerDateTime,
  toMinuteIsoString,
} from '../lib/scheduledAt';
import MailDialog from './MailDialog';
import MailMessageView from './MailMessageView';
import MailPanelHeader from './MailPanelHeader';
import type { MailRecipientsDropdownItem } from './MailRecipientsDropdown';
import MailStatusTag from './MailStatusTag';
import ScheduleDateTimeDialog from './ScheduleDateTimeDialog';

type CandidateMailDetailProps = {
  mailId: number;
  listRecord?: AgentApiCandidateMailsRecord;
  mailSettings: AgentApiOrganizationsMailSetting | null;
  onBack: () => void;
};

function buildRecipientItems(
  mail: AgentApiCandidateMailsRecord | AgentApiCandidateMailsShow
): MailRecipientsDropdownItem[] {
  const logs = 'candidate_mail_logs' in mail ? mail.candidate_mail_logs : [];
  if (logs.length > 0) {
    return logs.map((log) => ({
      key: log.id,
      label: `${log.candidate_profile_name || '（求職者名不明）'} <${log.address}>`,
      description: log.status === 'failed' && (
        <span className="text-danger">
          送信エラー{log.error_message ? `：${log.error_message}` : ''}
        </span>
      ),
    }));
  }
  return mail.recipient_names.map((name, index) => ({ key: `${index}-${name}`, label: name }));
}

function DetailTitle({ mail }: { mail: AgentApiCandidateMailsRecord }) {
  switch (mail.status) {
    case 'queued':
      return <span>送信待機中</span>;
    case 'processing':
      return <span>送信中</span>;
    case 'scheduled':
      return <span>{formatMailHeadingDate(mail.scheduled_at)} 送信予定</span>;
    case 'canceled':
      return (
        <>
          <MailStatusTag color="gray">キャンセル</MailStatusTag>
          <span className="truncate text-body-sm">
            {formatScheduledAt(mail.canceled_at)} に{' '}
            {mail.canceled_by?.name?.trim() || '削除されたユーザー'} がキャンセルしました
          </span>
        </>
      );
    case 'failed':
      return (
        <>
          <MailStatusTag color="red">送信エラー</MailStatusTag>
          <span className="truncate text-body-sm">
            {mail.error_message ||
              (mail.failed_count > 0 ? `${mail.failed_count}名への送信に失敗しました` : '')}
          </span>
        </>
      );
    default:
      return (
        <>
          <span>{formatMailHeadingDate(mail.sent_at)} 送信済み</span>
          {mail.failed_count > 0 && (
            <>
              <MailStatusTag color="orange">一部失敗</MailStatusTag>
              <span className="truncate text-body-sm">
                {mail.failed_count}名への送信に失敗しました
              </span>
            </>
          )}
        </>
      );
  }
}

export default function CandidateMailDetail({
  mailId,
  listRecord,
  mailSettings,
  onBack,
}: CandidateMailDetailProps) {
  const { data: mailDetail, isLoading, isError } = useFetchCandidateMail(mailId);
  const { mutateAsync: cancelCandidateMail, isPending: isCanceling } = useCancelCandidateMail();
  const { mutateAsync: rescheduleCandidateMail, isPending: isRescheduling } =
    useRescheduleCandidateMail();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const isCancelingRef = useRef(false);

  const mail = mailDetail ?? listRecord;

  if (!mail) {
    return (
      <div className="flex w-full flex-col">
        <MailPanelHeader onBack={onBack} title="" />
        <div className="flex justify-center py-24">
          {isLoading ? (
            <Loading size="small" />
          ) : (
            <p className="text-body-sm text-sub">
              {isError ? 'メールの取得に失敗しました。' : 'メールが見つかりません。'}
            </p>
          )}
        </div>
      </div>
    );
  }

  const isScheduled = mail.status === 'scheduled';
  const isQueued = mail.status === 'queued';

  const handleConfirmCancel = async () => {
    if (isCancelingRef.current) return;
    isCancelingRef.current = true;
    try {
      await cancelCandidateMail(mail.id);
      showSuccessToast(
        isScheduled ? '送信予約をキャンセルしました' : 'メールの送信をキャンセルしました'
      );
      setIsCancelConfirmOpen(false);
      onBack();
    } catch (error) {
      setIsCancelConfirmOpen(false);
      showErrorToast(
        error instanceof Error && error.message ? error.message : 'キャンセルに失敗しました。'
      );
    } finally {
      isCancelingRef.current = false;
    }
  };

  const handleReschedule = async (date: Date) => {
    try {
      await rescheduleCandidateMail({ mailId: mail.id, scheduledAt: toMinuteIsoString(date) });
      showSuccessToast('送信日時を変更しました');
      setIsRescheduleOpen(false);
    } catch (error) {
      showErrorToast(
        error instanceof Error && error.message ? error.message : '送信日時の変更に失敗しました。'
      );
    }
  };

  return (
    <div className="flex w-full flex-col">
      <MailPanelHeader
        onBack={onBack}
        title={<DetailTitle mail={mail} />}
        actions={
          isScheduled ? (
            <>
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={() => setIsRescheduleOpen(true)}
              >
                <MdSchedule className="size-4" />
                日時変更
              </Button>
              <Button
                type="button"
                color="danger"
                size="sm"
                onClick={() => setIsCancelConfirmOpen(true)}
              >
                予約キャンセル
              </Button>
            </>
          ) : isQueued ? (
            <Button
              type="button"
              color="danger"
              size="sm"
              onClick={() => setIsCancelConfirmOpen(true)}
            >
              送信キャンセル
            </Button>
          ) : undefined
        }
      />
      <div className="px-xl pb-xl">
        <MailMessageView
          recipientSummary={mail.recipient_names.join(', ')}
          recipientItems={buildRecipientItems(mail)}
          subject={mail.subject}
          senderName={mailSettings?.sender ?? ''}
          senderEmail={mailSettings?.reply_address ?? ''}
          body={mail.body ?? ''}
        />
      </div>

      <MailDialog
        open={isCancelConfirmOpen}
        onClose={() => setIsCancelConfirmOpen(false)}
        title="キャンセルの確認"
        preventClose={isCanceling}
        footer={
          <>
            <Button
              type="button"
              color="secondary"
              size="md"
              disabled={isCanceling}
              onClick={() => setIsCancelConfirmOpen(false)}
            >
              いいえ
            </Button>
            <Button
              type="button"
              color="danger"
              size="md"
              disabled={isCanceling}
              onClick={() => void handleConfirmCancel()}
            >
              はい
            </Button>
          </>
        }
      >
        <p className="text-body-sm text-main">
          {isScheduled
            ? `${formatScheduledAt(mail.scheduled_at)} の送信予約をキャンセルしてよろしいですか？`
            : '送信取り消ししてよろしいですか？'}
        </p>
      </MailDialog>

      {isScheduled && (
        <ScheduleDateTimeDialog
          open={isRescheduleOpen}
          title="送信日時を変更"
          initialValue={parseServerDateTime(mail.scheduled_at)}
          onClose={() => setIsRescheduleOpen(false)}
          onSubmit={handleReschedule}
          isSubmitting={isRescheduling}
        />
      )}
    </div>
  );
}
